import React, { useState, useEffect } from 'react';
import {
    TimesIcon,
    UserIcon,
    CalendarAltIcon,
    TrophyIcon,
    RocketIcon,
    BullhornIcon,
    CubesIcon,
    StarIcon,
    BoltIcon,
    HeartIcon,
    CrownIcon,
    FireIcon
} from '@patternfly/react-icons';
import { getUserInfo, getOrganizations, getCampaigns, getAds, getSubscription } from '@app/utils/utils';

interface MyProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    username: string;
}

interface ProfileStats {
    totalCampaigns: number;
    activeCampaigns: number;
    totalAds: number;
    totalOrganizations: number;
    creditsRemaining: number;
    memberSince: string;
}

const MyProfileModal: React.FC<MyProfileModalProps> = ({ isOpen, onClose, username }) => {
    const [stats, setStats] = useState<ProfileStats>({
        totalCampaigns: 0,
        activeCampaigns: 0,
        totalAds: 0,
        totalOrganizations: 0,
        creditsRemaining: 0,
        memberSince: ''
    });
    const [isLoading, setIsLoading] = useState(true);
    const [userEmail, setUserEmail] = useState('');
    const [hoveredBadge, setHoveredBadge] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            loadProfileData();
        }
    }, [isOpen]);

    const loadProfileData = async () => {
        setIsLoading(true);
        try {
            const [userInfo, orgsResult, campaignsResult, adsResult, subResult] = await Promise.all([
                getUserInfo(),
                getOrganizations().catch(() => ({ organizations: [] })),
                getCampaigns().catch(() => []),
                getAds().catch(() => []),
                getSubscription().catch(() => null)
            ]);

            const campaigns = campaignsResult || [];
            const activeCampaigns = campaigns.filter((c: any) => c.currentState === 'ACTIVE').length;

            const creditsRemaining = subResult
                ? Math.max(0, subResult.credits.limit - subResult.credits.used) + (subResult.credits.purchased ?? 0)
                : 0;

            setUserEmail(userInfo?.email || '');
            setStats({
                totalCampaigns: campaigns.length,
                activeCampaigns,
                totalAds: adsResult?.length || 0,
                totalOrganizations: orgsResult?.organizations?.length || 0,
                creditsRemaining,
                // Don't fabricate "now" when the API has no creation date — leave it
                // empty so the "Member since" row is hidden until a real date exists.
                // Field name matches the backend convention used elsewhere (Ads/Campaigns).
                memberSince: userInfo?.createDateTime || ''
            });
        } catch (error) {
            console.error('Failed to load profile data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    // Calculate user level based on activity
    const calculateLevel = () => {
        const points = stats.totalCampaigns * 100 + stats.totalAds * 50 + stats.activeCampaigns * 200;
        if (points >= 2000) return { level: 5, title: 'Ad Legend', icon: CrownIcon, color: '#fbbf24' };
        if (points >= 1000) return { level: 4, title: 'Campaign Master', icon: FireIcon, color: '#f97316' };
        if (points >= 500) return { level: 3, title: 'Marketing Pro', icon: StarIcon, color: '#a855f7' };
        if (points >= 200) return { level: 2, title: 'Rising Star', icon: RocketIcon, color: '#3b82f6' };
        return { level: 1, title: 'Newcomer', icon: HeartIcon, color: '#10b981' };
    };

    const levelInfo = calculateLevel();
    const LevelIcon = levelInfo.icon;

    // Fun achievement badges
    const badges = [
        {
            id: 'first-campaign',
            name: 'First Campaign',
            description: 'Launched your first campaign',
            icon: RocketIcon,
            color: '#10b981',
            earned: stats.totalCampaigns >= 1
        },
        {
            id: 'ad-creator',
            name: 'Ad Creator',
            description: 'Created 5+ ads',
            icon: CubesIcon,
            color: '#3b82f6',
            earned: stats.totalAds >= 5
        },
        {
            id: 'campaign-master',
            name: 'Campaign Master',
            description: 'Launched 10+ campaigns',
            icon: BullhornIcon,
            color: '#8b5cf6',
            earned: stats.totalCampaigns >= 10
        },
        {
            id: 'power-user',
            name: 'Power User',
            description: 'Running 3+ active campaigns',
            icon: BoltIcon,
            color: '#f59e0b',
            earned: stats.activeCampaigns >= 3
        },
        {
            id: 'organization-builder',
            name: 'Organization Builder',
            description: 'Created 2+ organizations',
            icon: TrophyIcon,
            color: '#ec4899',
            earned: stats.totalOrganizations >= 2
        }
    ];

    const earnedBadges = badges.filter(b => b.earned);
    const lockedBadges = badges.filter(b => !b.earned);

    // Format member since date
    const formatMemberSince = () => {
        if (!stats.memberSince) return 'Recently';
        const date = new Date(stats.memberSince);
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays < 1) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 30) return `${diffDays} days ago`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };

    const getInitial = () => {
        if (username && username.length > 0) {
            return username.charAt(0).toUpperCase();
        }
        return '?';
    };

    const styles = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            backdropFilter: 'blur(4px)'
        },
        modal: {
            background: '#ffffff',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '480px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.3)',
            animation: 'modalSlideIn 0.3s ease-out'
        },
        header: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            padding: '32px 24px 80px',
            position: 'relative' as const,
            borderRadius: '24px 24px 0 0'
        },
        closeButton: {
            position: 'absolute' as const,
            top: '16px',
            right: '16px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '12px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#ffffff',
            transition: 'all 0.2s',
            backdropFilter: 'blur(8px)'
        },
        headerContent: {
            textAlign: 'center' as const
        },
        levelBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(8px)',
            padding: '6px 14px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 600,
            color: '#ffffff',
            marginBottom: '8px'
        },
        headerTitle: {
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 500,
            opacity: 0.9,
            margin: 0
        },
        avatarSection: {
            position: 'relative' as const,
            marginTop: '-60px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'center'
        },
        avatarContainer: {
            position: 'relative' as const
        },
        avatar: {
            width: '120px',
            height: '120px',
            borderRadius: '28px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '48px',
            fontWeight: 700,
            border: '4px solid #ffffff',
            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
            animation: 'avatarPulse 3s ease-in-out infinite'
        },
        levelIndicator: {
            position: 'absolute' as const,
            bottom: '-8px',
            right: '-8px',
            width: '44px',
            height: '44px',
            borderRadius: '14px',
            background: levelInfo.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '20px',
            border: '3px solid #ffffff',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
        },
        body: {
            padding: '0 24px 24px'
        },
        nameSection: {
            textAlign: 'center' as const,
            marginBottom: '24px'
        },
        userName: {
            fontSize: '24px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 4px 0'
        },
        userEmail: {
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 8px 0'
        },
        userTitle: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: `linear-gradient(135deg, ${levelInfo.color}20 0%, ${levelInfo.color}10 100%)`,
            color: levelInfo.color,
            padding: '6px 14px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: 600
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px',
            marginBottom: '24px'
        },
        statCard: {
            background: '#f8fafc',
            borderRadius: '16px',
            padding: '16px',
            textAlign: 'center' as const,
            transition: 'all 0.2s',
            cursor: 'default',
            border: '2px solid transparent'
        },
        statCardHover: {
            background: '#f1f5f9',
            borderColor: '#667eea',
            transform: 'translateY(-2px)'
        },
        statIcon: {
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 8px',
            fontSize: '20px'
        },
        statValue: {
            fontSize: '24px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 2px 0'
        },
        statLabel: {
            fontSize: '12px',
            color: '#6b7280',
            fontWeight: 500
        },
        sectionTitle: {
            fontSize: '14px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        badgesSection: {
            marginBottom: '24px'
        },
        badgesGrid: {
            display: 'flex',
            flexWrap: 'wrap' as const,
            gap: '10px'
        },
        badge: {
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            position: 'relative' as const
        },
        badgeEarned: {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        },
        badgeLocked: {
            background: '#e5e7eb',
            color: '#9ca3af',
            filter: 'grayscale(100%)',
            opacity: 0.5
        },
        badgeTooltip: {
            position: 'absolute' as const,
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '8px',
            background: '#1f2937',
            color: '#ffffff',
            padding: '8px 12px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 500,
            whiteSpace: 'nowrap' as const,
            zIndex: 10,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
        },
        memberInfo: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '12px',
            color: '#6b7280',
            fontSize: '13px'
        },
        funFact: {
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderRadius: '16px',
            padding: '16px',
            marginTop: '16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
        },
        funFactIcon: {
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: '#fbbf24',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '18px',
            flexShrink: 0
        },
        funFactText: {
            fontSize: '13px',
            color: '#92400e',
            margin: 0,
            lineHeight: 1.5
        }
    };

    // Generate a fun fact based on stats
    const getFunFact = () => {
        if (stats.activeCampaigns > 0) {
            return `You have ${stats.activeCampaigns} active campaign${stats.activeCampaigns > 1 ? 's' : ''} running right now! Your ads are reaching audiences as we speak.`;
        }
        if (stats.totalCampaigns > 0) {
            return `You've launched ${stats.totalCampaigns} campaign${stats.totalCampaigns > 1 ? 's' : ''} so far. Ready to start your next one?`;
        }
        if (stats.totalAds > 0) {
            return `You have ${stats.totalAds} ad${stats.totalAds > 1 ? 's' : ''} ready to go. Time to create a campaign and put them to work!`;
        }
        return "Welcome to AdVolt! Create your first ad to start your advertising journey.";
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.header}>
                    <button
                        style={styles.closeButton}
                        onClick={onClose}
                        onMouseEnter={e => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                            e.currentTarget.style.transform = 'rotate(90deg)';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                            e.currentTarget.style.transform = 'rotate(0deg)';
                        }}
                    >
                        <TimesIcon />
                    </button>
                    <div style={styles.headerContent}>
                        <div style={styles.levelBadge}>
                            <LevelIcon /> Level {levelInfo.level}
                        </div>
                        <p style={styles.headerTitle}>Your Profile</p>
                    </div>
                </div>

                {/* Avatar Section */}
                <div style={styles.avatarSection}>
                    <div style={styles.avatarContainer}>
                        <div style={styles.avatar}>
                            {getInitial()}
                        </div>
                        <div style={styles.levelIndicator}>
                            <LevelIcon />
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div style={styles.body}>
                    {/* Name Section */}
                    <div style={styles.nameSection}>
                        <h2 style={styles.userName}>{username}</h2>
                        {userEmail && <p style={styles.userEmail}>{userEmail}</p>}
                        <span style={styles.userTitle}>
                            <LevelIcon /> {levelInfo.title}
                        </span>
                    </div>

                    {/* Stats Grid */}
                    <div style={styles.statsGrid}>
                        <div
                            style={styles.statCard}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = '#f1f5f9';
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = '#f8fafc';
                                e.currentTarget.style.borderColor = 'transparent';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <div style={{ ...styles.statIcon, background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: '#fff' }}>
                                <BullhornIcon />
                            </div>
                            <p style={styles.statValue}>{isLoading ? '-' : stats.totalCampaigns}</p>
                            <p style={styles.statLabel}>Campaigns</p>
                        </div>
                        <div
                            style={styles.statCard}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = '#f1f5f9';
                                e.currentTarget.style.borderColor = '#10b981';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = '#f8fafc';
                                e.currentTarget.style.borderColor = 'transparent';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <div style={{ ...styles.statIcon, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff' }}>
                                <RocketIcon />
                            </div>
                            <p style={styles.statValue}>{isLoading ? '-' : stats.activeCampaigns}</p>
                            <p style={styles.statLabel}>Active</p>
                        </div>
                        <div
                            style={styles.statCard}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = '#f1f5f9';
                                e.currentTarget.style.borderColor = '#8b5cf6';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = '#f8fafc';
                                e.currentTarget.style.borderColor = 'transparent';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <div style={{ ...styles.statIcon, background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', color: '#fff' }}>
                                <CubesIcon />
                            </div>
                            <p style={styles.statValue}>{isLoading ? '-' : stats.totalAds}</p>
                            <p style={styles.statLabel}>Ads Created</p>
                        </div>
                        <div
                            style={styles.statCard}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = '#f1f5f9';
                                e.currentTarget.style.borderColor = '#f59e0b';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = '#f8fafc';
                                e.currentTarget.style.borderColor = 'transparent';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <div style={{ ...styles.statIcon, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#fff' }}>
                                <BoltIcon />
                            </div>
                            <p style={styles.statValue}>{isLoading ? '-' : stats.creditsRemaining.toLocaleString()}</p>
                            <p style={styles.statLabel}>Credits Left</p>
                        </div>
                    </div>

                    {/* Badges Section */}
                    <div style={styles.badgesSection}>
                        <div style={styles.sectionTitle}>
                            <TrophyIcon style={{ color: '#fbbf24' }} /> Achievements
                        </div>
                        <div style={styles.badgesGrid}>
                            {badges.map(badge => {
                                const BadgeIcon = badge.icon;
                                return (
                                    <div
                                        key={badge.id}
                                        style={{
                                            ...styles.badge,
                                            background: badge.earned ? `linear-gradient(135deg, ${badge.color} 0%, ${badge.color}dd 100%)` : '#e5e7eb',
                                            color: badge.earned ? '#ffffff' : '#9ca3af',
                                            opacity: badge.earned ? 1 : 0.5,
                                            boxShadow: badge.earned ? `0 4px 12px ${badge.color}40` : 'none'
                                        }}
                                        onMouseEnter={() => setHoveredBadge(badge.id)}
                                        onMouseLeave={() => setHoveredBadge(null)}
                                    >
                                        <BadgeIcon />
                                        {hoveredBadge === badge.id && (
                                            <div style={styles.badgeTooltip}>
                                                <strong>{badge.name}</strong><br />
                                                <span style={{ opacity: 0.8, fontSize: '11px' }}>
                                                    {badge.earned ? badge.description : `Locked: ${badge.description}`}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Member Info — only shown when we have a real creation date */}
                    {stats.memberSince && (
                        <div style={styles.memberInfo}>
                            <CalendarAltIcon />
                            Member since {formatMemberSince()}
                        </div>
                    )}

                    {/* Fun Fact */}
                    <div style={styles.funFact}>
                        <div style={styles.funFactIcon}>
                            <StarIcon />
                        </div>
                        <p style={styles.funFactText}>{getFunFact()}</p>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes modalSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                @keyframes avatarPulse {
                    0%, 100% {
                        box-shadow: 0 12px 40px rgba(102, 126, 234, 0.4);
                    }
                    50% {
                        box-shadow: 0 12px 50px rgba(102, 126, 234, 0.6), 0 0 0 8px rgba(102, 126, 234, 0.1);
                    }
                }
            `}</style>
        </div>
    );
};

export { MyProfileModal };
